import React from 'react';
import { 
  Eye, 
  Plus, 
  Ruler, 
  Layers, 
  BarChart3, 
  Check, 
  X, 
  ZoomIn, 
  ZoomOut, 
  RefreshCw, 
  MapPin
} from 'lucide-react';

const MapToolbar = ({
  mode,
  onModeChange,
  onAddElement,
  onMeasure,
  onLayers,
  onAnalysis,
  onSave,
  onCancel,
  onZoomIn,
  onZoomOut,
  onReset,
  showLegend,
  onToggleLegend
}) => {
  const isActionActive = mode === 'add' || mode === 'measure' || mode === 'edit';

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Herramientas Rápidas
        </h4>
      </div>

      <div className="p-3 space-y-3">
        {/* Modes */}
        <div className="space-y-1">
          <p className="text-xs text-gray-500 font-medium px-2">Modo del Mapa</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => onModeChange('view')}
              className={`flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                mode === 'view'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              <span>Ver</span>
            </button>
            <button
              onClick={onMeasure}
              className={`flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                mode === 'measure'
                  ? 'bg-amber-600 text-white shadow-sm'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Ruler className="w-3.5 h-3.5" />
              <span>Medir</span>
            </button>
          </div>
        </div>

        {/* Add Element Quick Actions */}
        <div className="space-y-1">
          <p className="text-xs text-gray-500 font-medium px-2">Registrar Nuevo Punto</p>
          <div className="grid grid-cols-1 gap-1.5">
            <button
              onClick={() => onAddElement('Productor')}
              className="flex items-center justify-start space-x-2 px-3 py-2 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-blue-500" />
              <span>Registrar Productor</span>
            </button>
            <button
              onClick={() => onAddElement('Cultivo')}
              className="flex items-center justify-start space-x-2 px-3 py-2 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-green-500" />
              <span>Registrar Cultivo</span>
            </button>
            <button
              onClick={() => onAddElement('Centro de Acopio')}
              className="flex items-center justify-start space-x-2 px-3 py-2 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-red-500" />
              <span>Registrar Centro</span>
            </button>
          </div>
        </div>

        {/* Action Controls (Save / Cancel) */}
        {isActionActive && (
          <div className="pt-2 border-t border-gray-100 space-y-1.5">
            <p className="text-xs font-semibold text-amber-800 bg-amber-50 px-2 py-1.5 rounded-md flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
              Modo Dibujo/Medición Activo
            </p>
            <div className="flex gap-1.5">
              <button
                onClick={onSave}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors shadow"
              >
                <Check className="w-3.5 h-3.5" />
                <span>Guardar</span>
              </button>
              <button
                onClick={onCancel}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                <span>Cancelar</span>
              </button>
            </div>
          </div>
        )}

        {/* Map View Adjustments */}
        <div className="pt-2 border-t border-gray-100 space-y-1.5">
          <p className="text-xs text-gray-500 font-medium px-2">Ajustes de Vista</p>
          <div className="flex justify-between gap-1.5">
            <button
              onClick={onZoomIn}
              className="flex-1 flex items-center justify-center p-2 rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              title="Acercar"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={onZoomOut}
              className="flex-1 flex items-center justify-center p-2 rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              title="Alejar"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={onReset}
              className="flex-1 flex items-center justify-center p-2 rounded-md bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              title="Restaurar Centro"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={onToggleLegend}
            className={`w-full flex items-center justify-center space-x-1.5 px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
              showLegend
                ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                : 'border-transparent bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <span>{showLegend ? 'Ocultar Leyenda' : 'Mostrar Leyenda'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapToolbar;
